// policy.ts — pure PR detection + unit/format helpers, ported verbatim in spirit
// from the Flutter app's lib/src/policy/{pr.dart,units.dart}. No I/O, no network.
// The Rust db layer computes the persisted PR flags; these helpers cover display +
// any client-side preview.

const LB_PER_KG = 2.2046226218;

export const lbToKg = (lb: number): number => lb / LB_PER_KG;
export const kgToLb = (kg: number): number => kg * LB_PER_KG;

export function convert(value: number, from: "lb" | "kg", to: "lb" | "kg"): number {
  if (from === to) return value;
  if (from === "lb" && to === "kg") return lbToKg(value);
  if (from === "kg" && to === "lb") return kgToLb(value);
  throw new Error(`unsupported unit pair: ${from} -> ${to}`);
}

/** Epley 1RM estimate. reps==1 → the weight itself; reps<=0 or weight<=0 → 0. */
export function estimateOneRepMax(weight: number, reps: number): number {
  if (reps <= 0 || weight <= 0) return 0;
  if (reps === 1) return weight;
  return weight * (1 + reps / 30);
}

/** Epley + nearest-0.5 rounding — used in the PR card. */
export function roundedOneRepMax(weight: number, reps: number): number {
  return Math.round(estimateOneRepMax(weight, reps) * 2) / 2;
}

/** 100 → "100", 100.5 → "100.5", 102.25 → "102.25". */
export function formatWeight(w: number): string {
  if (w === Math.round(w)) return w.toFixed(0);
  if (Math.round(w * 10) === w * 10) return w.toFixed(1);
  return w.toFixed(2);
}

/** "102.5 lb"; "46.5 kg" — rounds to one decimal, drops trailing .0. */
export function displayWeight(value: number, unit: string): string {
  const rounded = Math.round(value * 10) / 10;
  const s = rounded === Math.round(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
  return `${s} ${unit}`;
}

/** 65 → "1:05", 3725 → "1:02:05". */
export function formatDuration(seconds: number): string {
  if (seconds < 0) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const ss = String(s).padStart(2, "0");
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${ss}`;
  return `${m}:${ss}`;
}

const PR_LABELS: Record<string, string> = {
  estimatedOneRepMax: "1RM",
  "1RM": "1RM",
  heaviestWeight: "heaviest",
  heaviest: "heaviest",
  bestRepsAtWeight: "reps at weight",
  reps: "reps at weight",
  bestVolume: "volume",
  volume: "volume",
};

/** Ordered, human-readable PR labels (1RM > heaviest > reps > volume). */
export function prLabels(types: string[]): string[] {
  const order = ["estimatedOneRepMax", "1RM", "heaviestWeight", "heaviest", "bestRepsAtWeight", "reps", "bestVolume", "volume"];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const key of order) {
    if (types.includes(key)) {
      const label = PR_LABELS[key];
      if (label && !seen.has(label)) { seen.add(label); out.push(label); }
    }
  }
  return out;
}
